import { useMemo } from 'react';
import { Progress } from '@/components/ui/progress';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export interface PasswordRequirement {
  label: string;
  met: boolean;
}

export const usePasswordStrength = (password: string) => {
  return useMemo(() => {
    const requirements: PasswordRequirement[] = [
      {
        label: 'At least 8 characters',
        met: password.length >= 8
      },
      {
        label: 'Contains uppercase letter',
        met: /[A-Z]/.test(password)
      },
      {
        label: 'Contains lowercase letter',
        met: /[a-z]/.test(password)
      },
      {
        label: 'Contains number',
        met: /\d/.test(password)
      },
      {
        label: 'Contains special character',
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      }
    ];

    const metCount = requirements.filter(req => req.met).length;
    const strength = (metCount / requirements.length) * 100;

    let strengthLabel = 'Very Weak';
    let strengthColor = 'bg-destructive';

    if (metCount >= 4) {
      strengthLabel = 'Strong';
      strengthColor = 'bg-green-500';
    } else if (metCount >= 3) {
      strengthLabel = 'Medium';
      strengthColor = 'bg-yellow-500';
    } else if (metCount >= 2) {
      strengthLabel = 'Weak';
      strengthColor = 'bg-orange-500';
    }

    const isValid = metCount >= 4; // Require at least 4 out of 5 criteria

    return {
      requirements,
      strength,
      strengthLabel,
      strengthColor,
      isValid,
      metCount
    };
  }, [password]);
};

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const { requirements, strength, strengthLabel, strengthColor, isValid } = usePasswordStrength(password);

  if (!password) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Password Strength</span>
          <span className={`font-medium ${
            isValid ? 'text-green-600' : 'text-orange-600'
          }`}>
            {strengthLabel}
          </span>
        </div>
        <Progress 
          value={strength} 
          className="h-2"
        />
      </div>
      
      <div className="space-y-1">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            {req.met ? (
              <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
            ) : (
              <X className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            )}
            <span className={req.met ? 'text-green-700' : 'text-muted-foreground'}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;